import * as Rx from 'rxjs';
import { tap, switchMap, debounceTime, finalize, take } from 'rxjs/operators';

import type { IAffiliation, IProductAffiliation } from 'v2/interfaces/affiliation';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';

import apiService from '../api';

import type { ApiService } from '../api';

export const InitialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10,
};

export class AffiliationsService {
  private loading$ = new Rx.BehaviorSubject<boolean>(true);
  private affiliations$ = new Rx.BehaviorSubject<Array<IAffiliation>>([]);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private loadingProductRecruitment$ = new Rx.BehaviorSubject<boolean>(true);
  private productRecruitment$ = new Rx.BehaviorSubject<IProductAffiliation>(null);

  private loadingInviteRecruitment$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService) {}

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public isLoading = () => this.loading$;

  public getAffiliations = (): Rx.Observable<Array<IAffiliation>> => this.affiliations$;

  public loadAffiliations = () => {
    return this.getPaginationParams().pipe(
      debounceTime(10),
      switchMap((pagination) => {
        this.loading$.next(true);

        return this.apiService
          .get('/affiliations', { per_page: pagination.per_page, current_page: pagination.current_page })
          .pipe(finalize(() => this.loading$.next(false)));
      }),
      tap((response) => this.affiliations$.next(response.data)),
      tap((response) => this.paginationResponse$.next(response.pagination))
    );
  };

  public updateAffiliations = () => {
    this.getPaginationResponse()
      .pipe(
        switchMap((pagination) => {
          this.loading$.next(true);

          return this.apiService
            .get('/affiliations', { per_page: pagination.per_page, current_page: pagination.current_page })
            .pipe(finalize(() => this.loading$.next(false)));
        }),
        tap((response) => this.affiliations$.next(response.data)),
        tap((response) => {
          if (response.pagination && response.pagination.current_page > response.pagination.total_pages) {
            this.loading$.next(true);
            this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
            return;
          }

          this.paginationResponse$.next(response.pagination);
        }),
        take(1)
      )
      .subscribe();
  };

  public isLoadingRecruitment = () => this.loadingProductRecruitment$;

  public getProductRecruitment = (): Rx.Observable<IProductAffiliation> => this.productRecruitment$;

  public loadProductById(id: number) {
    this.loadingProductRecruitment$.next(true);

    return this.apiService.get(`/affiliates/products/${id}`).pipe(
      tap((response) => this.productRecruitment$.next(response.data)),
      finalize(() => this.loadingProductRecruitment$.next(false))
    );
  }

  public inviteAffiliationByEmail = (product: IProductAffiliation, email: string) => {
    this.loadingInviteRecruitment$.next(true);

    return this.apiService
      .post(`/products/${product.id}/affiliate`, { email, indication: true })
      .pipe(finalize(() => this.loadingInviteRecruitment$.next(false)));
  };

  public cancelAffilation = (affiliation: IAffiliation) => {
    this.loading$.next(true);
    return this.apiService.delete(`/affiliations/${affiliation.id}`).pipe(tap(() => this.updateAffiliations()));
  };
}

const affiliationsService = new AffiliationsService(apiService);
export default affiliationsService;
